import { unwrap, type components } from "@sagot/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "./engage-keys";

export type AgentSummary = components["schemas"]["AgentSummaryOut"];
export type Audience = components["schemas"]["AudienceOut"];

const REFERENCE_STALE_MS = 5 * 60_000;

/** `GET /v1/agents`: agent names for filters, pickers and labels. */
export function useAgentList(): UseQueryResult<AgentSummary[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: engageKeys.agents(wsId),
    queryFn: async () => (await unwrap(api.GET("/v1/agents"))).items,
    staleTime: REFERENCE_STALE_MS,
  });
}

/** `GET /v1/audiences`: tenant audience labels (codes → labels; never hard-coded wording). */
export function useAudienceList(): UseQueryResult<Audience[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: engageKeys.audiences(wsId), queryFn: () => unwrap(api.GET("/v1/audiences")), staleTime: REFERENCE_STALE_MS });
}

/** Agent id → name lookup (empty while agents load or for unknown ids). */
export function useAgentName(): (agentId: string | null | undefined) => string {
  const agents = useAgentList();
  return (agentId) => (agentId ? (agents.data?.find((a) => a.id === agentId)?.name ?? "") : "");
}
